import { confirmar } from '@/components/ui/confirm-dialog';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Loader2, Save } from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import {
  freightProviderService,
  FreightProvider,
  CreateFreightProviderData,
  FreightProviderType,
  FREIGHT_PROVIDER_TYPE_LABELS,
} from '../services/freight-provider-service';
import { TransportMode, TRANSPORT_MODE_LABELS } from '../types/freight-route.types';

const ALL_TRANSPORT_MODES: TransportMode[] = ['SEA', 'AIR', 'LAND', 'RAIL', 'MULTIMODAL'];

export function FreightProviderTable() {
  const { toast } = useToast();
  const [providers, setProviders] = useState<FreightProvider[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingProvider, setEditingProvider] = useState<FreightProvider | null>(null);

  const fetchProviders = async () => {
    setIsLoading(true);
    try {
      const data = await freightProviderService.getAll();
      setProviders(data);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const handleDelete = async (provider: FreightProvider) => {
    if (
      !(await confirmar({
        title: `¿Desactivar el proveedor "${provider.name}"?`,
        variant: 'destructive',
        confirmText: 'Sí, desactivar',
      }))
    )
      return;
    try {
      await freightProviderService.delete(provider.id);
      toast({ title: 'Proveedor desactivado', variant: 'success' });
      fetchProviders();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          onClick={() => {
            setEditingProvider(null);
            setShowCreateDialog(true);
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Proveedor
        </Button>
      </div>

      {providers.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No hay proveedores de transporte configurados. Crea uno para empezar.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>País</TableHead>
              <TableHead>NIT</TableHead>
              <TableHead>Modos</TableHead>
              <TableHead>Contacto</TableHead>
              <TableHead className="text-center">Crédito</TableHead>
              <TableHead className="text-center">★</TableHead>
              <TableHead className="text-center">Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {providers.map((provider) => {
              const p = provider as any;
              const contactBits = [p.email, p.phone, p.contactName].filter(Boolean);
              return (
                <TableRow key={provider.id}>
                  <TableCell className="font-medium">
                    {provider.name}
                    {p.legalName && (
                      <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                        {p.legalName}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    {p.providerType ? (
                      <Badge variant="outline">
                        {FREIGHT_PROVIDER_TYPE_LABELS[p.providerType as FreightProviderType] ||
                          p.providerType}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{p.countryCode || '—'}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {p.taxId || '—'}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {provider.transportModes.map((mode) => (
                        <Badge key={mode} variant="outline">
                          {TRANSPORT_MODE_LABELS[mode as TransportMode] || mode}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {contactBits.length > 0 ? contactBits.join(' · ') : provider.contactInfo || '—'}
                  </TableCell>
                  <TableCell className="text-center text-xs">
                    {p.creditTermsDays != null ? `${p.creditTermsDays}d` : '—'}
                  </TableCell>
                  <TableCell className="text-center">
                    {p.rating != null ? '★'.repeat(p.rating) : '—'}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={provider.isActive ? 'default' : 'secondary'}>
                      {provider.isActive ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setEditingProvider(provider);
                          setShowCreateDialog(true);
                        }}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(provider)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* Create/Edit Provider Dialog */}
      <ProviderFormDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        provider={editingProvider}
        onSaved={fetchProviders}
      />
    </div>
  );
}

// ========================================
// Provider Form Dialog
// ========================================

function ProviderFormDialog({
  open,
  onOpenChange,
  provider,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider: FreightProvider | null;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState('');
  // Tipo de actor (naviera, NVOCC, aduana, seguro, …). '' = sin clasificar.
  const [providerType, setProviderType] = useState<string>('');
  const [transportModes, setTransportModes] = useState<string[]>([]);
  const [contactInfo, setContactInfo] = useState('');
  const [sapCode, setSapCode] = useState('');
  // Fiscal
  const [taxId, setTaxId] = useState('');
  const [legalName, setLegalName] = useState('');
  const [fiscalAddress, setFiscalAddress] = useState('');
  // Contacto
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [contactName, setContactName] = useState('');
  const [website, setWebsite] = useState('');
  // Banco
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountType, setAccountType] = useState('');
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [swiftCode, setSwiftCode] = useState('');
  // Operativos
  const [countryCode, setCountryCode] = useState('');
  const [serviceArea, setServiceArea] = useState('');
  const [creditTermsDays, setCreditTermsDays] = useState<string>('');
  const [rating, setRating] = useState<string>('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (provider) {
      const p = provider as any;
      setName(p.name);
      setProviderType(p.providerType || '');
      setTransportModes(p.transportModes);
      setContactInfo(p.contactInfo || '');
      setSapCode(p.sapCode || '');
      setTaxId(p.taxId || '');
      setLegalName(p.legalName || '');
      setFiscalAddress(p.fiscalAddress || '');
      setEmail(p.email || '');
      setPhone(p.phone || '');
      setWhatsapp(p.whatsapp || '');
      setContactName(p.contactName || '');
      setWebsite(p.website || '');
      setBankName(p.bankName || '');
      setAccountNumber(p.accountNumber || '');
      setAccountType(p.accountType || '');
      setBeneficiaryName(p.beneficiaryName || '');
      setSwiftCode(p.swiftCode || '');
      setCountryCode(p.countryCode || '');
      setServiceArea(p.serviceArea || '');
      setCreditTermsDays(p.creditTermsDays != null ? String(p.creditTermsDays) : '');
      setRating(p.rating != null ? String(p.rating) : '');
      setNotes(p.notes || '');
    } else {
      setName('');
      setProviderType('');
      setTransportModes([]);
      setContactInfo('');
      setSapCode('');
      setTaxId('');
      setLegalName('');
      setFiscalAddress('');
      setEmail('');
      setPhone('');
      setWhatsapp('');
      setContactName('');
      setWebsite('');
      setBankName('');
      setAccountNumber('');
      setAccountType('');
      setBeneficiaryName('');
      setSwiftCode('');
      setCountryCode('');
      setServiceArea('');
      setCreditTermsDays('');
      setRating('');
      setNotes('');
    }
  }, [provider, open]);

  const toggleMode = (mode: string) => {
    setTransportModes((prev) =>
      prev.includes(mode) ? prev.filter((m) => m !== mode) : [...prev, mode]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (transportModes.length === 0) {
      toast({
        title: 'Error',
        description: 'Selecciona al menos un modo de transporte',
        variant: 'destructive',
      });
      return;
    }
    setIsSaving(true);
    try {
      const parseIntOrNull = (v: string): number | null => {
        if (!v.trim()) return null;
        const n = parseInt(v, 10);
        return Number.isNaN(n) ? null : n;
      };
      const data: CreateFreightProviderData = {
        name,
        providerType: (providerType as FreightProviderType) || null,
        transportModes,
        contactInfo: contactInfo || null,
        sapCode: sapCode.trim() || null,
        // Fiscal
        taxId: taxId.trim() || null,
        legalName: legalName.trim() || null,
        fiscalAddress: fiscalAddress.trim() || null,
        // Contacto
        email: email.trim() || null,
        phone: phone.trim() || null,
        whatsapp: whatsapp.trim() || null,
        contactName: contactName.trim() || null,
        website: website.trim() || null,
        // Banco
        bankName: bankName.trim() || null,
        accountNumber: accountNumber.trim() || null,
        accountType: accountType.trim() || null,
        beneficiaryName: beneficiaryName.trim() || null,
        swiftCode: swiftCode.trim() || null,
        // Operativos
        countryCode: countryCode.trim().toUpperCase() || null,
        serviceArea: serviceArea.trim() || null,
        creditTermsDays: parseIntOrNull(creditTermsDays),
        rating: parseIntOrNull(rating),
        notes: notes.trim() || null,
      };
      if (provider) {
        await freightProviderService.update(provider.id, data);
        toast({ title: 'Proveedor actualizado', variant: 'success' });
      } else {
        await freightProviderService.create(data);
        toast({ title: 'Proveedor creado', variant: 'success' });
      }
      onOpenChange(false);
      onSaved();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {provider ? 'Editar Proveedor' : 'Nuevo Proveedor de Transporte'}
          </DialogTitle>
          <DialogDescription>
            Nombre, modos de transporte y datos fiscales/contacto/bancarios/operativos
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* ── Datos básicos ── */}
          <section className="space-y-3">
            <p className="text-xs font-semibold uppercase text-muted-foreground">Datos básicos</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Nombre comercial *</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="MSC, Maersk, DHL..."
                  required
                />
              </div>
              <div>
                <Label>País (ISO)</Label>
                <Input
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value.toUpperCase())}
                  placeholder="BO, US, CL..."
                  maxLength={2}
                />
              </div>
            </div>
            <div>
              <Label>Tipo de actor</Label>
              <select
                value={providerType}
                onChange={(e) => setProviderType(e.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">— Sin clasificar —</option>
                {(Object.keys(FREIGHT_PROVIDER_TYPE_LABELS) as FreightProviderType[]).map((t) => (
                  <option key={t} value={t}>
                    {FREIGHT_PROVIDER_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                Qué es en la cadena: naviera, NVOCC, transportista, aerolínea, agencia aduanera,
                aseguradora… Así la cotización se puede desglosar por actor.
              </p>
            </div>
            <div>
              <Label>Modos de Transporte *</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {ALL_TRANSPORT_MODES.map((mode) => (
                  <Badge
                    key={mode}
                    variant={transportModes.includes(mode) ? 'default' : 'outline'}
                    className="cursor-pointer select-none"
                    onClick={() => toggleMode(mode)}
                  >
                    {TRANSPORT_MODE_LABELS[mode]}
                  </Badge>
                ))}
              </div>
            </div>
          </section>

          {/* ── Datos fiscales ── */}
          <section className="rounded-md border border-blue-200 bg-blue-50/30 p-3 space-y-3">
            <p className="text-xs font-semibold uppercase text-blue-700">Datos fiscales</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>NIT / RUT / CUIT</Label>
                <Input
                  value={taxId}
                  onChange={(e) => setTaxId(e.target.value)}
                  placeholder="1234567890"
                  maxLength={64}
                />
              </div>
              <div>
                <Label>Razón social</Label>
                <Input
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  placeholder="Empresa S.A."
                  maxLength={255}
                />
              </div>
            </div>
            <div>
              <Label>Dirección fiscal</Label>
              <Input
                value={fiscalAddress}
                onChange={(e) => setFiscalAddress(e.target.value)}
                placeholder="Av. Principal #123, Ciudad, País"
              />
            </div>
          </section>

          {/* ── Contacto estructurado ── */}
          <section className="rounded-md border p-3 space-y-3">
            <p className="text-xs font-semibold uppercase text-muted-foreground">Contacto</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Persona de contacto</Label>
                <Input
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Juan Pérez"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ventas@empresa.com"
                />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+591 3 333 4444"
                />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  placeholder="+591 700 12345"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Sitio web</Label>
                <Input
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://empresa.com"
                />
              </div>
            </div>
            <details>
              <summary className="text-xs text-muted-foreground cursor-pointer">
                Información de contacto en texto libre (legado)
              </summary>
              <Input
                className="mt-2"
                value={contactInfo}
                onChange={(e) => setContactInfo(e.target.value)}
                placeholder="Texto libre adicional (opcional)"
              />
            </details>
          </section>

          {/* ── Datos bancarios ── */}
          <section className="rounded-md border border-emerald-200 bg-emerald-50/20 p-3 space-y-3">
            <p className="text-xs font-semibold uppercase text-emerald-700">Datos bancarios</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Banco</Label>
                <Input
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  placeholder="Banco Mercantil Santa Cruz"
                />
              </div>
              <div>
                <Label>Titular de la cuenta</Label>
                <Input
                  value={beneficiaryName}
                  onChange={(e) => setBeneficiaryName(e.target.value)}
                  placeholder="Empresa S.A."
                />
              </div>
              <div>
                <Label>Número de cuenta</Label>
                <Input
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="000123456789"
                />
              </div>
              <div>
                <Label>Tipo de cuenta</Label>
                <Input
                  value={accountType}
                  onChange={(e) => setAccountType(e.target.value)}
                  placeholder="CHECKING / SAVINGS / OTHER"
                  maxLength={50}
                />
              </div>
              <div>
                <Label>SWIFT / BIC</Label>
                <Input
                  value={swiftCode}
                  onChange={(e) => setSwiftCode(e.target.value.toUpperCase())}
                  placeholder="BNBOBEBO"
                  maxLength={20}
                />
              </div>
            </div>
          </section>

          {/* ── Operativos / comerciales ── */}
          <section className="rounded-md border p-3 space-y-3">
            <p className="text-xs font-semibold uppercase text-muted-foreground">
              Operativo / comercial
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Días de crédito</Label>
                <Input
                  type="number"
                  min="0"
                  max="365"
                  value={creditTermsDays}
                  onChange={(e) => setCreditTermsDays(e.target.value)}
                  placeholder="30"
                />
              </div>
              <div>
                <Label>Rating (1-5)</Label>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  value={rating}
                  onChange={(e) => setRating(e.target.value)}
                  placeholder="4"
                />
              </div>
              <div>
                <Label>Código SAP</Label>
                <Input
                  value={sapCode}
                  onChange={(e) => setSapCode(e.target.value)}
                  placeholder="Código en SAP"
                  maxLength={64}
                />
              </div>
            </div>
            <div>
              <Label>Rutas / zonas que cubre</Label>
              <Input
                value={serviceArea}
                onChange={(e) => setServiceArea(e.target.value)}
                placeholder="China → Bolivia, USA → Bolivia"
              />
            </div>
            <div>
              <Label>Notas internas</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Observaciones, condiciones especiales..."
              />
            </div>
          </section>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Save className="w-4 h-4 mr-2" />
              {provider ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
